(() => {
  'use strict';
  if (window.SVTracking) return;
  const configNode = document.getElementById('sv-tracking-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const privatePath = /\/(account|checkouts?|orders|password)(\/|$)/i;
  if (privatePath.test(location.pathname)) return;
  let sdk;
  let loading = false;
  let pageCaptured = false;
  let allowed = false;
  const intents = new Map();
  const seen = new WeakSet();
  const rapidClicks = new WeakMap();
  let cartBound = false;
  let errorCount = 0;
  const identityCookie = 'sv_ph_distinct';
  const shareIdentity = () => {
    const id = sdk?.get_distinct_id?.();
    if (allowed && consent() && typeof id === 'string' && /^[a-zA-Z0-9_-]{16,100}$/.test(id)) {
      document.cookie = `${identityCookie}=${encodeURIComponent(id)}; Path=/; Max-Age=2592000; SameSite=Lax; Secure`;
    }
  };
  const clearIdentity = () => { document.cookie = `${identityCookie}=; Path=/; Max-Age=0; SameSite=Lax; Secure`; };
  const privateSelector = 'input,textarea,select,[data-private],.ph-no-capture,.customer,.account,.cart-item__properties,[data-cart-note],[data-newsletter-popup-error],.template-search__header,.predictive-search-result--queries,.search__results-all,.search__box .empty-state';
  const blockedSelector = 'form[action*="contact"],form[action*="account"],a[href*="account.spicevillage.eu"],iframe,.shopify-challenge__container,[id*="chat"],[id*="Chat"],[data-private],.ph-no-capture';
  const safeUrl = (value) => {
    try {
      const url = new URL(value, location.origin);
      return privatePath.test(url.pathname) ? url.origin + '/[private]' : url.origin + url.pathname;
    } catch (_) { return ''; }
  };
  const consent = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch (_) { return false; }
  };
  const capture = (event, properties = {}) => {
    if (!allowed || !consent() || !sdk) return;
    try {
      sdk.capture(event, {
        ...properties,
        environment: config.environment,
        login_status: config.login_status,
        page_type: config.page_type,
        schema_version: 1,
      });
    } catch (_) { /* Measurement must never interrupt shopping. */ }
  };
  const capturePage = () => {
    if (pageCaptured) return;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', capturePage, { once: true });
      return;
    }
    pageCaptured = true;
    capture('$pageview', { $current_url: safeUrl(location.href) });
    if (config.product_id) capture('storefront_product_viewed', { product_id: config.product_id });
    if (config.collection_id) capture('storefront_collection_viewed', { collection_id: config.collection_id });
    if (config.page_type === 'cart') capture('storefront_cart_viewed');
    if (config.page_type === 'search') {
      const root = document.querySelector('.template-search[data-search-performed="true"]');
      if (root) {
        const resultCount = Number(root.dataset.searchResultsCount);
        const visibleProductCount = root.querySelectorAll('#ProductsList .product-card').length;
        const properties = {
          result_count: Number.isFinite(resultCount) ? resultCount : undefined,
          visible_product_count: visibleProductCount,
        };
        capture('storefront_search_results_viewed', properties);
        if (resultCount === 0) capture('storefront_search_zero_results', properties);
      }
    }
  };
  const syncConsent = () => {
    allowed = consent();
    document.documentElement.dataset.svTracking = allowed ? (sdk ? 'active' : 'loading') : 'consent-denied';
    if (!allowed) {
      intents.clear();
      clearIdentity();
      if (sdk) { sdk.stopSessionRecording(); sdk.opt_out_capturing(); }
      return;
    }
    if (sdk) {
      sdk.opt_in_capturing({ captureEventName: false });
      shareIdentity();
      sdk.startSessionRecording();
      document.documentElement.dataset.svReplay = String(sdk.sessionRecordingStarted());
      capturePage();
      return;
    }
    if (loading) return;
    loading = true;
    const script = document.createElement('script');
    script.src = 'https://us-assets.i.posthog.com/static/array.js';
    script.async = true;
    script.onerror = () => { loading = false; };
    script.onload = () => {
      loading = false;
      if (!consent() || !window.posthog) return;
      window.posthog.init(config.token, {
        api_host: config.host,
        ui_host: 'https://us.posthog.com',
        defaults: '2026-05-30',
        capture_pageview: false,
        capture_pageleave: true,
        autocapture: false,
        person_profiles: 'never',
        ip: false,
        disable_geoip: true,
        disable_surveys: true,
        enable_recording_console_log: false,
        capture_performance: false,
        session_recording: {
          maskAllInputs: true,
          maskAllElementAttributes: true,
          maskTextSelector: privateSelector,
          blockSelector: blockedSelector,
          recordCrossOriginIframes: false,
          recordCanvas: false,
          recordHeaders: false,
          recordBody: false,
          maskNetworkRequestFn: () => null,
        },
        before_send: (event) => {
          if (!consent()) return null;
          const properties = event.properties || {};
          for (const key of Object.keys(properties)) {
            if (/url|referrer/i.test(key) && typeof properties[key] === 'string') properties[key] = safeUrl(properties[key]);
            if (/email|phone|name|address|initial_utm|initial_referring|search_term|\$set/i.test(key)) delete properties[key];
          }
          properties.environment = config.environment;
          properties.login_status = config.login_status;
          properties.page_type = config.page_type;
          return event;
        },
        loaded: (instance) => { sdk = instance; syncConsent(); },
      });
    };
    document.head.appendChild(script);
  };
  const surface = (element) => {
    if (element?.closest('quick-view,quick-view-modal,[id*="QuickView"]')) return 'quick_view';
    if (element?.closest('predictive-search,[id*="predictive-search"]')) return 'search';
    return ({ product: 'product_page', collection: 'collection', index: 'homepage', search: 'search_results' })[config.page_type] || config.page_type || 'unknown';
  };
  const controlType = (element) => {
    if (element.closest('form[action*="/cart/add"]')) return 'add_to_cart';
    if (element.closest('predictive-search')) return 'search';
    if (element.matches('[name="checkout"]')) return 'checkout';
    if (element.closest('.product-card')) return 'product_card';
    return 'other';
  };
  document.addEventListener('click', (event) => {
    if (!allowed) return;
    const element = event.target.closest('button,a');
    const thumbnail = event.target.closest('.product__thumbs-item[data-media-position]');
    if (thumbnail?.dataset?.mediaPosition) capture('storefront_product_gallery_thumbnail_clicked', { position: Number(thumbnail.dataset.mediaPosition) });
    const summary = event.target.closest('.clean-product-details details > summary');
    if (summary?.parentElement && !summary.parentElement.open) {
      capture('storefront_product_information_opened', { section_number: [...summary.parentElement.parentElement.querySelectorAll('details')].indexOf(summary.parentElement) + 1 });
    }
    if (!element) return;
    const recent = rapidClicks.get(element) || { count: 0, last: 0, reported: 0 };
    const now = Date.now();
    recent.count = now - recent.last < 1500 ? recent.count + 1 : 1;
    recent.last = now;
    if (recent.count >= 3 && now - recent.reported > 5000) {
      capture('storefront_rapid_click', { control: controlType(element), surface: surface(element) });
      recent.reported = now;
    }
    rapidClicks.set(element, recent);
    const form = element.closest('form[action*="/cart/add"]');
    if (form) {
      const variant = form.querySelector('[name="id"]')?.value;
      const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
      if (variant) {
        intents.set(String(variant), { surface: surface(element), quantity, timestamp: Date.now() });
        capture('storefront_add_to_cart_attempted', { variant_id: variant, quantity, surface: surface(element) });
      }
    }
    const link = element.closest('a[href]');
    if (link) {
      const path = new URL(link.href, location.origin).pathname;
      const suggestion = link.closest('#predictive-search-results-queries-list li');
      if (suggestion) {
        const rank = [...suggestion.parentElement.querySelectorAll('li')].indexOf(suggestion) + 1;
        capture('storefront_search_suggestion_clicked', { result_rank: rank });
      }
      if (path.includes('/products/')) {
        const list = link.closest('#predictive-search-results-products-list,#ProductsList');
        const card = link.closest('.product-card');
        const cards = list?.querySelectorAll('.product-card');
        const rank = card && cards ? [...cards].indexOf(card) + 1 : 0;
        capture('storefront_product_clicked', { destination: safeUrl(link.href), surface: surface(element), result_rank: rank || undefined });
      }
      if (path.includes('/collections/')) capture('storefront_collection_clicked', { destination: safeUrl(link.href) });
      if (path === '/cart') capture('storefront_cart_opened');
      if (path === '/checkout') capture('storefront_checkout_intent');
    }
    if (element.matches('[name="checkout"]')) capture('storefront_checkout_intent');
  });
  document.addEventListener('submit', (event) => {
    if (event.target.matches('form[action*="/search"]')) {
      const queryLength = event.target.querySelector('[name="q"]')?.value?.trim().length;
      capture('storefront_search_submitted', { query_length: queryLength, surface: surface(event.target) });
    }
  });
  document.addEventListener('storefront:predictive-search-results', (event) => {
    const detail = event.detail || {};
    const properties = {
      product_count: detail.product_count,
      suggestion_count: detail.suggestion_count,
      collection_count: detail.collection_count,
      query_length: detail.query_length,
      cached: detail.cached,
      duration_ms: detail.duration_ms,
    };
    capture('storefront_predictive_results_viewed', properties);
    if (detail.product_count === 0 && detail.suggestion_count === 0 && detail.collection_count === 0) {
      capture('storefront_predictive_zero_results', properties);
    }
  });
  document.addEventListener('storefront:predictive-search-error', () => capture('storefront_predictive_search_error'));
  document.addEventListener('change', (event) => {
    if (event.target.matches('[name="sort_by"],[name^="filter."]')) capture('storefront_collection_filter_changed', { control: event.target.name });
    if (event.target.matches('[name="id"],variant-selects select,variant-picker input')) capture('storefront_variant_changed');
    if (event.target.matches('form[action*="/cart/add"] [name="quantity"]')) capture('storefront_product_quantity_selected', { quantity: Number(event.target.value) });
  });
  document.addEventListener('product-ajax:added', (event) => {
    const response = event.detail?.product;
    if (!response || typeof response !== 'object' || seen.has(response)) return;
    seen.add(response);
    for (const item of response.items || [response]) {
      const pending = intents.get(String(item.variant_id || item.id));
      const intent = pending && Date.now() - pending.timestamp < 60000 ? pending : null;
      capture('storefront_add_to_cart_succeeded', {
        product_id: item.product_id,
        variant_id: item.variant_id || item.id,
        quantity: intent?.quantity,
        cart_line_quantity: item.quantity,
        surface: intent?.surface || 'unknown',
        event_id: window.crypto.randomUUID(),
      });
      intents.delete(String(item.variant_id || item.id));
    }
  });
  const bindCartEvents = () => {
    if (cartBound || !window.FoxTheme?.pubsub) return;
    cartBound = true;
    window.FoxTheme.pubsub.subscribe(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, () => capture('storefront_cart_error'));
    window.FoxTheme.pubsub.subscribe(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (data) => {
      const cart = data?.cart;
      if (cart) capture('storefront_cart_updated', { item_count: cart.item_count, total_minor: cart.total_price, currency: cart.currency, action: data?.name === 'remove' ? 'remove' : data?.name ? 'quantity_change' : 'other' });
    });
  };
  bindCartEvents();
  document.addEventListener('DOMContentLoaded', bindCartEvents, { once: true });
  window.addEventListener('error', (event) => {
    if (errorCount++ >= 3) return;
    const url = event.filename ? safeUrl(event.filename) : '';
    capture('storefront_js_error', { kind: 'uncaught', source: url.startsWith(location.origin) ? url.split('/').pop() : 'external' });
  });
  window.addEventListener('unhandledrejection', () => {
    if (errorCount++ < 3) capture('storefront_js_error', { kind: 'unhandled_rejection' });
  });
  document.addEventListener('visitorConsentCollected', syncConsent);
  document.addEventListener('newsletter:signup-completed', () => capture('storefront_newsletter_signup_completed', { surface: 'popup' }));
  window.SVTracking = { capture, status: () => ({ allowed: consent(), loaded: !!sdk, replay: !!sdk?.sessionRecordingStarted(), environment: config.environment }) };
  if (window.Shopify?.customerPrivacy) syncConsent();
  else if (window.Shopify?.loadFeatures) window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => { if (!error) syncConsent(); });
})();
